import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { FiCamera, FiKey } from 'react-icons/fi';
import { getSecureItemFromSpecificStorage } from '../lib/BrowserStorageAccessMiddleware';
import { ACCESS_GROUP_BANKS, LOCAL_STORAGE,  LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, LS_LOCKER_BANK_INFO, PERMISSIONS_LIST } from '../assets/constants/BrowserStorageKeys';
import ButtonComponent from '../components/ButtonComponent';
import CustomQRCodeReader from '../components/CustomQRCodeReader';
import CustomDialog from '../components/CustomDialog';
import DoorAssignedUserDetailsModal from '../components/DoorAssignedUserDetailsModal';
import CircularProgressLoader from '../components/CircularProgressLoader';
import ShowToast from '../components/ToastComponent';
import { BUTTON_PRIMARY, BUTTON_SECONDARY, BUTTON_WARNING, TOAST_ERROR, TOAST_SUCCESS, SUCCESS } from '../assets/constants/Constants';
import {  getDomainAndLockerBankInfoFromQRCodeData, formatDateTime, isPodBankType, getUnitLabelParamsFromBool } from '../lib/Utils';
import { DoorDetails, releaseLockerOfPickUpUser } from '../api/api';

export default function AdminLockerDoorOperation() {
    const { t } = useTranslation()
    const history = useHistory()
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(t('pleaseWaitText'));
    const [showQRCodeReader, setShowQRCodeReader] = useState(false)
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogContentText, setDialogContentText] = useState('');
    const [scannedUserDetails, setScannedUserDetails] = useState(null);
    const [userDetailsDialogVisible, setUserDetailsDialogVisible] = useState(false);
    const [releaseConfirmVisible, setReleaseConfirmVisible] = useState(false);
    const permissionsList = getSecureItemFromSpecificStorage(LOCAL_STORAGE, PERMISSIONS_LIST);
    const accessGroupBanks = getSecureItemFromSpecificStorage(LOCAL_STORAGE, ACCESS_GROUP_BANKS);

    const tenantAndAssignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const isVerifyWithQRCodeEnabled = tenantAndAssignmentSettings?.usecase_settings?.verify_with_qr_code_pwa?.value;
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const isPodLockerType = isPodBankType(lockerBankInfo?.LockerBankType ?? lockerBankInfo?.bank_type);
    const unitLabelParams = getUnitLabelParamsFromBool(isPodLockerType, t);

    const handleScanQRCode = () => {
        setShowQRCodeReader(true)
    }

    const qrReaderScanHandler = async (qrCodeData) => {
        try {
            if (qrCodeData && qrCodeData.length > 0) {
                setLoadingMessage(t('msg_Getting_Door_Details'));
                setIsLoading(true);
                setShowQRCodeReader(false);
                const domainAndLockerBankInfoObject = getDomainAndLockerBankInfoFromQRCodeData(qrCodeData);
                if (domainAndLockerBankInfoObject) {
                   // have to call the api to get the assing locker door details of user and then sow that data to a component
                   const payload = {
                        "locker_bank_id": domainAndLockerBankInfoObject?.lockerBankId,
                        "door_no": domainAndLockerBankInfoObject?.lockerDoorNo,
                   }
                
                   const hasAccessToLockerBank = accessGroupBanks?.some(
                        (bank) => String(bank?.id) === String(domainAndLockerBankInfoObject?.lockerBankId)
                   );

                   if (!hasAccessToLockerBank) {
                        setIsLoading(false);
                        ShowToast(t('error_msg_no_access_to_locker_bank', unitLabelParams), TOAST_ERROR);
                        return;
                   }

                   let response = null;
                   try {
                        response  = await DoorDetails(payload);
                   }catch (error) {
                    console.error("AdminLockerDoorOperation.js - qrReaderScanHandler DoorDetails API error: ", error);
                   }

                   const assignedReservation = response?.data?.reservation;

                   if (!assignedReservation || assignedReservation?.door_status !== "Assigned") {
                        setIsLoading(false);
                        ShowToast(t('error_msg_no_active_transaction', unitLabelParams), TOAST_ERROR);
                        return;
                   }

                   const assignedUserDetails = assignedReservation?.user_details;

                   setScannedUserDetails({
                        full_name: assignedUserDetails?.name,
                        email: assignedUserDetails?.email || assignedReservation?.customer_email,
                        phone: assignedUserDetails?.mobile_number || assignedReservation?.customer_mobile_number,
                        user_attrs: assignedUserDetails?.user_attrs,
                        locker_bank_name: assignedReservation?.locker_bank_name,
                        door_number: assignedReservation?.door_number,
                        door_size: assignedReservation?.door_size,
                        assigned_date: formatDateTime(assignedReservation?.assigned_date),
                        // kept for release-locker action, not rendered in the modal
                        transaction_id: assignedReservation?.transaction_id,
                    });
                    setUserDetailsDialogVisible(true);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                    ShowToast(t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR)
                    return;
                }
            }
        } catch (error) {
            setIsLoading(false);
            ShowToast(t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR)
            console.error("AdminLockerDoorOperation.js - qrReaderScanHandler", error)
        }
    }

    const handleCloseModal = () => {
        setDialogVisible(false)
    }

    const handleUserDetailsOkClick = () => {
        setUserDetailsDialogVisible(false);
    }

    const handleUserDetailsReleaseLockerClick = () => {
        setReleaseConfirmVisible(true);
    }

    const handleReleaseConfirmCancel = () => {
        setReleaseConfirmVisible(false);
    }

    const handleReleaseConfirmAccept = async () => {
        const transactionId = scannedUserDetails?.transaction_id;
        setReleaseConfirmVisible(false);
        setUserDetailsDialogVisible(false);
        if (!transactionId) {
            ShowToast(t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR);
            return;
        }
        try {
            setLoadingMessage(t('pleaseWaitText'));
            setIsLoading(true);
            const releaseLockerResponse = await releaseLockerOfPickUpUser(transactionId);
            if (releaseLockerResponse?.status === SUCCESS) {
                ShowToast(releaseLockerResponse.message, TOAST_SUCCESS);
            } else {
                ShowToast(releaseLockerResponse?.message || t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR);
            }
        } catch (error) {
            console.error("AdminLockerDoorOperation.js - handleReleaseConfirmAccept releaseLockerOfPickUpUser API error: ", error);
            ShowToast(t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR);
        } finally {
            setIsLoading(false);
            setScannedUserDetails(null);
        }
    }

    return (
        <div className="adminLockerDoorOperation-container">
            <div className='main-container'>
            {isLoading && <CircularProgressLoader message={loadingMessage} />}
            {showQRCodeReader ?
                <div className="qr-scan-card" id="admin-door-qr-scan-card">
                    <div className="qr-scan-card-header">
                        <span className="qr-scan-icon">
                            <FiCamera size={18} />
                        </span>
                        <div className="qr-scan-header-text">
                            <p className="eyebrow">{t('scanQRCode')}</p>
                            <p className="subtitle">{t('accessLockerBankByQR', unitLabelParams)}</p>
                        </div>
                    </div>
                    <div className="qr-code-reader-container">
                        <div className="qr-code-reader">
                            <CustomQRCodeReader
                                handleOnScan={qrReaderScanHandler}
                            />
                        </div>
                    </div>
                    <div className="qr-scan-card-actions">
                        <ButtonComponent buttonId="cancel-scan-QR-button" handleClick={() => setShowQRCodeReader(false)} title={'button_cancel'} type={BUTTON_SECONDARY} />
                    </div>
                </div>
                :
                <div className="bottom-actions-container">
                    {isVerifyWithQRCodeEnabled && permissionsList?.includes("can_access_mobile_app") &&
                        <ButtonComponent buttonId="scan-QR-button" handleClick={handleScanQRCode} title={"Scan QR Code"} type={BUTTON_PRIMARY} />
                    }
                </div>
            }
            <DoorAssignedUserDetailsModal
                visible={userDetailsDialogVisible}
                userDetails={scannedUserDetails}
                handleClose={handleUserDetailsOkClick}
                handleReleaseLocker={handleUserDetailsReleaseLockerClick}
                isPodLockerType={isPodLockerType}
            />
            <CustomDialog
                dialogId='error-dialog'
                dialogVisible={dialogVisible}
                handleAccept={handleCloseModal}
                dialogTitle={t('page_title_error')}
                dialogContentText={t(dialogContentText)}
                buttonTwoTitle={t('button_ok')}
                buttonProps={{ buttonTwo: { type: BUTTON_PRIMARY } }}
            />
            <CustomDialog
                dialogId='release-door-confirm-dialog'
                dialogVisible={releaseConfirmVisible}
                onClose={handleReleaseConfirmCancel}
                handleCancel={handleReleaseConfirmCancel}
                handleAccept={handleReleaseConfirmAccept}
                dialogTitle={t('confirm_Text')}
                dialogContentText={t('confirm_release_door_text')}
                buttonOneTitle={t('button_cancel')}
                buttonTwoTitle={t('button_release_locker', unitLabelParams)}
                buttonContainerClassName={"button-containers"}
                buttonProps={{ buttonOne: { type: BUTTON_SECONDARY }, buttonTwo: { type: BUTTON_WARNING, startIcon: <FiKey size={16} /> } }}
            />
            </div>
        </div>
    )
}
